import type { ComponentChildren } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { AlertTriangle, ChartNoAxesColumn, Clock, Eye, EyeOff, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-preact'
import { websApi, type WebViewDto } from '@/api/webs'
import { webinarRoomApi, type WebinarRoomPollViewDto } from '@/api/webinarRoom'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { retryOnStaleETag } from '@/utils/etagRetry'
import { sessionLengthSeconds } from '@/utils/webinarRoomSetup'
import { RoomPollForm } from './RoomPollForm'
import { RoomPollPreview } from './RoomPollPreview'
import { RoomPollTimeline } from './RoomPollTimeline'
import { LOCK_TITLE } from './roomRecording'
import {
    applyPollChange, draftOf, findSavedPoll, formatPollTime, pollFromDraft, pollRowNote, pollTimeline, sortPolls, staysUpRowText,
    type PollChange, type PollDraft, type PollDraftCheck,
} from './roomPolls'
import { BUTTON, CARD, CARD_SUB, CARD_TITLE, ICON_BUTTON } from './roomStyles'

/** How long a deleted poll can be brought back. */
export const UNDO_SECONDS = 8
/** A saved or pointed-at row lights up, then fades back over the row's own transition. */
const FLASH_MS = 60

type FormState = { mode: 'new' } | { mode: 'edit'; id: string }

interface RoomPollsCardProps {
    web: WebViewDto
    isSignedOff: boolean
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
    /** The brand's primary colour for the viewer's-eye previews, or null for the neutral skin. */
    accent: string | null
}

/**
 * The Room tab's Polls card (ORB-1088): polls written ahead, each at a time in the recording, on a timeline. Added
 * and edited in place, deleted with eight seconds to undo, previewed as viewers see them. Every change saves the
 * whole list with the web's ETag; a 412 rereads the web and applies the same change to what is there now.
 */
export function RoomPollsCard({ web, isSignedOff, onPatchWeb, accent }: RoomPollsCardProps) {
    const { canEdit } = useWorkspaceCapabilities()
    const { showErrorUnless422 } = useToast()
    const event = web.OnlineEvent!
    const polls = sortPolls(event.Polls ?? [])
    const readyRecordingSeconds = event.Recording?.State === 'Ready' ? event.Recording.DurationSeconds ?? null : null
    const sessionSeconds = sessionLengthSeconds(event.StartDateTime, event.EndDateTime)
    const timeline = pollTimeline(sessionSeconds, readyRecordingSeconds)

    const [form, setForm] = useState<FormState | null>(null)
    const [ghostSeconds, setGhostSeconds] = useState<number | null>(null)
    const [saving, setSaving] = useState(false)
    const [undo, setUndo] = useState<WebinarRoomPollViewDto | null>(null)
    const [previewId, setPreviewId] = useState<string | null>(null)
    const [highlightIds, setHighlightIds] = useState<string[]>([])
    const [flashIds, setFlashIds] = useState<string[]>([])
    const [focusId, setFocusId] = useState<string | null>(null)
    const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const rows = useRef(new Map<string, HTMLElement>())

    const canChange = canEdit && !isSignedOff
    const editingId = form?.mode === 'edit' ? form.id : null

    useEffect(() => () => { if (undoTimer.current) clearTimeout(undoTimer.current) }, [])

    useEffect(() => {
        if (!flashIds.length) return
        const timer = setTimeout(() => setFlashIds([]), FLASH_MS)
        return () => clearTimeout(timer)
    }, [flashIds])

    useEffect(() => {
        if (!focusId) return
        document.getElementById(focusId)?.focus()
        setFocusId(null)
    }, [focusId])

    const flash = (ids: string[]) => {
        setFlashIds(ids)
        rows.current.get(ids[0])?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }

    const save = async (change: PollChange): Promise<WebViewDto | null> => {
        setSaving(true)
        const res = await retryOnStaleETag(
            document => webinarRoomApi.savePolls(document.WorkspaceId, document.Id, { ETag: document.ETag, Polls: applyPollChange(document.OnlineEvent?.Polls ?? [], change) }),
            web,
            async () => {
                const fresh = await websApi.getById(web.WorkspaceId, web.Id)
                if (!fresh.data) return null
                const latest = fresh.data
                onPatchWeb(() => latest)
                return latest
            },
        ).finally(() => setSaving(false))
        if (!res.data) {
            showErrorUnless422('Polls not saved', res, 'The polls could not be saved.')
            return null
        }
        const saved = res.data
        onPatchWeb(prev => ({ ...prev, ...saved }))
        return saved
    }

    const closeForm = () => {
        setForm(null)
        setGhostSeconds(null)
    }

    const submit = async (draft: PollDraft, check: PollDraftCheck) => {
        const values = pollFromDraft(draft, check)
        const change: PollChange = form?.mode === 'edit' ? { kind: 'edit', id: form.id, poll: values } : { kind: 'add', poll: values }
        const saved = await save(change)
        if (!saved) return
        closeForm()
        const poll = findSavedPoll(saved.OnlineEvent?.Polls ?? [], change)
        if (poll) {
            flash([poll.Id])
            setFocusId(`room-poll-edit-${poll.Id}`)
        }
    }

    const remove = async (poll: WebinarRoomPollViewDto) => {
        const saved = await save({ kind: 'delete', id: poll.Id })
        if (!saved) return
        if (previewId === poll.Id) setPreviewId(null)
        if (undoTimer.current) clearTimeout(undoTimer.current)
        setUndo(poll)
        undoTimer.current = setTimeout(() => setUndo(null), UNDO_SECONDS * 1000)
        setFocusId('room-poll-undo')
    }

    const restore = async () => {
        if (!undo) return
        const change: PollChange = {
            kind: 'add',
            poll: { Question: undo.Question, Options: undo.Options, OffsetSeconds: undo.OffsetSeconds, StaysUpSeconds: undo.StaysUpSeconds ?? null },
        }
        const saved = await save(change)
        if (!saved) return
        if (undoTimer.current) clearTimeout(undoTimer.current)
        setUndo(null)
        const poll = findSavedPoll(saved.OnlineEvent?.Polls ?? [], change)
        if (poll) {
            flash([poll.Id])
            setFocusId(`room-poll-edit-${poll.Id}`)
        }
    }

    const openForm = (next: FormState) => {
        setPreviewId(null)
        setGhostSeconds(null)
        setForm(next)
    }

    const ghostFor = (seconds: number | null): number | null => {
        if (seconds === null || !timeline || seconds >= timeline.lengthSeconds) return null
        const original = editingId ? polls.find(poll => poll.Id === editingId) : undefined
        return original && original.OffsetSeconds === seconds ? null : seconds
    }

    const formFor = (poll?: WebinarRoomPollViewDto) => (
        <RoomPollForm
            key={poll?.Id ?? 'new'}
            initial={draftOf(poll)}
            isNew={!poll}
            others={poll ? polls.filter(other => other.Id !== poll.Id) : polls}
            timeline={timeline}
            sessionSeconds={sessionSeconds}
            saving={saving}
            accent={accent}
            onTimeChange={seconds => setGhostSeconds(ghostFor(seconds))}
            onSave={(draft, check) => void submit(draft, check)}
            onCancel={() => {
                closeForm()
                setFocusId(poll ? `room-poll-edit-${poll.Id}` : 'room-poll-add')
            }}
        />
    )

    const rowFor = (poll: WebinarRoomPollViewDto, index: number) => {
        if (editingId === poll.Id) return <div key={poll.Id}>{formFor(poll)}</div>
        const note = pollRowNote(poll, polls[index + 1], timeline)
        const at = formatPollTime(poll.OffsetSeconds)
        const lit = highlightIds.includes(poll.Id)
        const flashed = flashIds.includes(poll.Id)
        const open = previewId === poll.Id
        const actionTitle = (title: string) => (isSignedOff ? LOCK_TITLE : title)
        return (
            <div
                key={poll.Id}
                ref={element => { if (element) rows.current.set(poll.Id, element); else rows.current.delete(poll.Id) }}
                data-testid={`room-poll-row-${poll.Id}`}
                className={`grid grid-cols-[52px_minmax(0,1fr)_auto] gap-x-3.5 gap-y-1 items-start -mx-2 px-2 py-3.5 rounded-md border-b border-slate-200 dark:border-slate-700 last:border-b-0 ${flashed
                    ? 'bg-primary-100 dark:bg-primary-900/40'
                    : `transition-colors duration-700 ${lit ? 'bg-slate-50 dark:bg-slate-900/40' : 'hover:bg-slate-50 dark:hover:bg-slate-900/40'}`}`}
                onMouseEnter={() => setHighlightIds([poll.Id])}
                onMouseLeave={() => setHighlightIds([])}
            >
                <span className="text-sm font-semibold leading-[22px] tabular-nums text-slate-700 dark:text-slate-200">{at}</span>
                <div className="min-w-0">
                    <p className="font-medium leading-[22px] text-slate-900 dark:text-white break-words">{poll.Question}</p>
                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400 break-words">{poll.Options.join(' · ')}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <Clock className="w-3.5 h-3.5 flex-none" />
                        {staysUpRowText(poll.StaysUpSeconds)}
                    </p>
                    {note && (
                        <p className={`mt-1.5 flex items-center gap-1.5 text-xs ${note.tone === 'error' ? 'text-red-700 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>
                            <AlertTriangle className={`w-3.5 h-3.5 flex-none ${note.tone === 'error' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                            {note.text}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <button type="button" className={`${BUTTON} border-transparent px-2`} aria-expanded={open} onClick={() => setPreviewId(open ? null : poll.Id)}>
                        {open ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {open ? 'Hide preview' : 'Preview'}
                    </button>
                    {canEdit && (
                        <>
                            <button
                                type="button" id={`room-poll-edit-${poll.Id}`} className={ICON_BUTTON} aria-label={`Edit the poll at ${at}`} title={actionTitle('Edit')}
                                disabled={!canChange || !!form || saving} onClick={() => openForm({ mode: 'edit', id: poll.Id })}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button" className={`${ICON_BUTTON} hover:text-red-600`} aria-label={`Delete the poll at ${at}`} title={actionTitle('Delete')}
                                disabled={!canChange || !!form || saving} onClick={() => void remove(poll)}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                </div>
                {open && (
                    <div className="col-start-2 col-end-4 mt-2.5">
                        <RoomPollPreview question={poll.Question} options={poll.Options} offsetSeconds={poll.OffsetSeconds} staysUpSeconds={poll.StaysUpSeconds ?? null} accent={accent} />
                    </div>
                )}
            </div>
        )
    }

    const list: ComponentChildren[] = polls.map(rowFor)
    if (undo) {
        const at = polls.findIndex(poll => poll.OffsetSeconds > undo.OffsetSeconds)
        list.splice(at < 0 ? list.length : at, 0, (
            <div key="undo" role="status" className="my-2.5 flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300">
                <Trash2 className="w-3.5 h-3.5 flex-none" />
                <span>Deleted the poll at {formatPollTime(undo.OffsetSeconds)}.</span>
                <button type="button" id="room-poll-undo" className={`${BUTTON} ml-auto`} disabled={saving} onClick={() => void restore()}>
                    <RotateCcw className="w-3.5 h-3.5" />Undo
                </button>
            </div>
        ))
    }

    const newForm = form?.mode === 'new' ? formFor() : null

    return (
        <section className={CARD} aria-labelledby="room-polls-title">
            <div className="flex items-center justify-between gap-3">
                <h2 id="room-polls-title" className={CARD_TITLE}>
                    <ChartNoAxesColumn className="w-5 h-5 text-primary-600" /> Polls
                    <span className="inline-block min-w-[22px] px-[7px] rounded-full bg-slate-100 dark:bg-slate-700 text-center text-xs font-semibold leading-5 text-slate-600 dark:text-slate-300">{polls.length}</span>
                </h2>
                {canEdit && (
                    <button type="button" id="room-poll-add" className={BUTTON} disabled={!canChange || !!form || saving} title={isSignedOff ? LOCK_TITLE : undefined} onClick={() => openForm({ mode: 'new' })}>
                        <Plus className="w-3.5 h-3.5" />Add poll
                    </button>
                )}
            </div>
            <p className={CARD_SUB}>Each one pops up on its own at its time in the recording. The moderator can also launch any of them at any point from the room.</p>
            {timeline && (
                <RoomPollTimeline
                    timeline={timeline}
                    polls={polls}
                    ghostSeconds={ghostSeconds}
                    highlightIds={highlightIds}
                    onHover={ids => setHighlightIds(ids ?? [])}
                    onMarker={flash}
                />
            )}
            {newForm}
            {polls.length || undo ? (
                <div className="mt-[18px] border-t border-slate-200 dark:border-slate-700">{list}</div>
            ) : !newForm && (
                <div className="mt-[18px] border-t border-slate-200 dark:border-slate-700 px-4 py-7 text-center text-sm">
                    <p className="font-medium text-slate-600 dark:text-slate-300">No polls yet.</p>
                    <p className="text-slate-500 dark:text-slate-400">Add one and pick when it pops up in the recording.</p>
                </div>
            )}
        </section>
    )
}
